import { zodResolver } from "@hookform/resolvers/zod"
import { Box, Collapse, IconButton, Paper, Typography } from "@mui/material"
import {
  AddOutlined,
  EditOutlined,
  KeyboardArrowDownOutlined,
  KeyboardArrowUpOutlined,
} from "@mui-symbols-material/w300"
import { useParams, useSearch } from "@tanstack/react-router"
import type { FC, ReactElement } from "react"
import { useCallback, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { useMutation } from "urql"

import { useProductTechnicalInformation } from "../hooks/useProductTechnicalnformation"
import { type TechnicalInformationFormData, TechnicalInformationSchema } from "../schemas/TechnicalInformationSchema"
import { toTechnicalInformationUpdateInput } from "../utils/technicalInformation"

import AddTechnicalInformationModal from "./AddTechnicalInformationModal"
import { TechnicalInformationList } from "./TechnicalInformationList"

import { InformationMissingAlert } from "@/components/common/Alerts/InformationMissingAlert"
import { Button } from "@/components/common/Button"
import {
  type Mutation,
  type ProductMutationUpdateArgs,
  type ProductUpdateFailure,
  UpdateProductMutationDocument,
} from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import { isNil } from "@/utils/utils"

const defaultTechnicalInformation: TechnicalInformationFormData = {
  chemicalName: "",
  specificGravity: undefined,
  shelfLifeMonths: 0,
  harmonizedTariffSchedule: "",
  usmcaClass: false,
  unNumber: "",
  hazardStatusUs: undefined,
  hazardStatusInternational: undefined,
}

interface ProductTechnicalInformationContainerProps {
  isPastProductVersion: boolean
}

export const ProductTechnicalInformationContainer: FC<ProductTechnicalInformationContainerProps> = ({
  isPastProductVersion,
}): ReactElement => {
  const [expanded, setExpanded] = useState(true)
  const [addTechnicalModal, setAddTechnicalModal] = useState(false)
  const { enqueueNotification } = useNotificationsStore()
  const methods = useForm<TechnicalInformationFormData>({
    defaultValues: defaultTechnicalInformation,
    resolver: zodResolver(TechnicalInformationSchema),
    mode: "onChange",
  })
  const { productId: queryCompanyId } = useParams({
    from: "/app/product/$productId/edit",
  })
  const { version } = useSearch({ from: "/app/product/$productId/edit/_layout/information" })

  const { data: technicalInformation, fetch: fetchTechnicalInformation } = useProductTechnicalInformation({
    productId: Number(queryCompanyId),
    version,
  })
  const [, updateProduct] = useMutation<Pick<Mutation, "product">, ProductMutationUpdateArgs>(
    UpdateProductMutationDocument
  )
  const { setError, reset, trigger } = methods

  const showTechnicalInformation =
    technicalInformation?.chemicalName ||
    technicalInformation?.specificGravity ||
    technicalInformation?.shelfLifeMonths ||
    technicalInformation?.casRegistryIds ||
    technicalInformation?.harmonizedTariffSchedule ||
    technicalInformation?.usmcaClass ||
    technicalInformation?.unNumber ||
    !isNil(technicalInformation?.hazardStatusUs) ||
    !isNil(technicalInformation?.hazardStatusInternational)

  const canEditTechnicalInformation = showTechnicalInformation && expanded && !isPastProductVersion

  const handleSubmit = useCallback(
    async (data: TechnicalInformationFormData) => {
      try {
        const result = await updateProduct({
          input: toTechnicalInformationUpdateInput(data, {
            productId: Number(queryCompanyId),
            versionNumber: version,
          }),
        })

        if (result.error || (result.data?.product.update as ProductUpdateFailure)?.error) {
          throw new Error(
            result.error?.message || (result.data?.product.update as ProductUpdateFailure)?.error?.message
          )
        }

        enqueueNotification({
          type: NotificationType.Success,
          silent: false,
          content: (
            <p>Product technical specification successfully {showTechnicalInformation ? "updated" : "added"}.</p>
          ),
        })
        reset()
        setAddTechnicalModal(false)
        fetchTechnicalInformation({ productId: Number(queryCompanyId), version })
      } catch (error) {
        setError("root", {
          type: "server",
          message: (error as Error).message,
        })

        enqueueNotification({
          type: NotificationType.Error,
          silent: false,
          content: <p>Failed to {showTechnicalInformation ? "update" : "add"} the product technical specification</p>,
        })
      }
    },
    [
      updateProduct,
      queryCompanyId,
      version,
      enqueueNotification,
      showTechnicalInformation,
      reset,
      fetchTechnicalInformation,
      setError,
    ]
  )

  const onEditHandleClick = useCallback(() => {
    if (!technicalInformation) return // Handle possible null value
    reset({
      chemicalName: technicalInformation.chemicalName || "",
      specificGravity: technicalInformation.specificGravity ?? undefined,
      shelfLifeMonths: technicalInformation.shelfLifeMonths,
      harmonizedTariffSchedule: technicalInformation.harmonizedTariffSchedule || "",
      usmcaClass: Boolean(technicalInformation.usmcaClass),
      unNumber: technicalInformation.unNumber || "",
      hazardStatusUs: technicalInformation.hazardStatusUs,
      hazardStatusInternational: technicalInformation.hazardStatusInternational,
    })
    trigger()
    setAddTechnicalModal(true)
  }, [reset, trigger, technicalInformation])

  return (
    <Paper className='flex flex-1 flex-col border border-gray-400 p-6' elevation={0}>
      <Box className={`flex justify-between ${expanded ? "mb-6" : ""}`}>
        <Typography variant='h6'>Technical Specifications</Typography>
        <Box>
          {canEditTechnicalInformation && (
            <IconButton color='primary' aria-label='edit product conditions' onClick={onEditHandleClick}>
              <EditOutlined />
            </IconButton>
          )}
          <IconButton onClick={() => setExpanded(!expanded)} aria-expanded={expanded} aria-label='show more'>
            {expanded ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
          </IconButton>
        </Box>
      </Box>
      <Collapse in={expanded}>
        {showTechnicalInformation ? (
          <TechnicalInformationList productDetails={technicalInformation} />
        ) : (
          <InformationMissingAlert title='Add Technical Information' description=''>
            {!isPastProductVersion && (
              <Button
                color='inherit'
                size='small'
                appearance='outlined'
                className='self-start text-primary'
                startIcon={<AddOutlined />}
                onClick={() => {
                  reset(defaultTechnicalInformation)
                  setAddTechnicalModal(true)
                }}
              >
                Technical Details
              </Button>
            )}
          </InformationMissingAlert>
        )}
        <FormProvider {...methods}>
          <AddTechnicalInformationModal
            open={addTechnicalModal}
            onClose={() => {
              reset()
              setAddTechnicalModal(false)
            }}
            onSubmit={handleSubmit}
          />
        </FormProvider>
      </Collapse>
    </Paper>
  )
}
